import React from "react"
import { Block, Text } from "@convertkit/slate"
import { useIcon } from "../../icon-context"

export const LabelStyle = ({ label, icon }) => {
  const Icon = icon
  return (
    <div className="flex items-center text-sm text-gray-900">
      <span className="mr-3 inline-flex justify-center text-gray-700">
        <Icon />
      </span>
      {label}
    </div>
  )
}

export const createMenuItem = ({ onSelect, icon, label, ...attributes }) => {
  const onOptionSelect = (editor, block, insertingFromBlockAbove) => {
    // These are text-like nodes: Nodes that include text in them
    const textNodes = [
      "Blockquote",
      "Button",
      "Divider",
      "Heading",
      "Number List",
      "Paragraph",
      "Unordered List"
    ]

    // Text-like nodes need new insertion
    // do not insert any paragraph when it is not a text-like node.
    if (textNodes.includes(label)) {
      const index = editor.value.document.nodes.findIndex(
        node => node.key === block.key
      )

      const node = Block.create({
        type: "paragraph",
        nodes: [Text.create()]
      })

      const i = insertingFromBlockAbove ? 0 : index + 1

      editor
        .insertNodeByKey(editor.value.document.key, i, node)
        .moveToRangeOfNode(node)
    } else {
      editor.moveToEndOfNode(block.nodes.last())
    }

    onSelect(editor, block)
  }

  const Label =
    attributes.labelComponent ||
    function() {
      const Icon = useIcon(icon, true)
      return <LabelStyle label={label} icon={Icon} />
    }

  return {
    onSelect: onOptionSelect,
    label: <Label />,
    ...attributes
  }
}

export const createInsertersFor = array =>
  array.map(item => {
    return createMenuItem({
      onSelect: () => null,
      icon: item.menuIcon,
      label: item.label,
      type: item.type,
      operation: item.operation
    })
  })

/*
 * @param {ElementNode} element the element to get position of
 * @param {boolean} bounded determines if the top position is relative to top of window or top of document
 * @example `getDOMPosition(el)` the top value is based on its distance from the start of document regardless of scroll
 * @example `getDOMPosition(el, true)` the top value is based on top of window viewport (best used with position:fixed)
 * @returns {object}
 */
export function getDOMPosition(element) {
  if (!element) return {}

  const rect = element.getBoundingClientRect()

  return {
    width: rect.width,
    height: rect.height,
    top: rect.top + document.documentElement.scrollTop,
    left: rect.left + document.documentElement.scrollLeft,
    bottom: rect.bottom + document.documentElement.scrollTop,
    right: rect.right + document.documentElement.scrollLeft,
    bounded: rect
  }
}

export function findDOMSlateNode(node) {
  if (!node) return null
  return document.querySelector(`[data-key="${node.key}"]`)
}

/**
 * Gets the DOM element of a block
 * @param {Object} block
 * @returns
 */
export function findDOMBlock(block) {
  const domElement = document.querySelector(
    `[data-block-id="${block.block_id}"]`
  )
  if (!domElement) {
    throw new Error(
      `dom element not found for block with block id ${block.block_id}`
    )
  }
  return domElement
}

export function getMenuPosition(direction, CBDimension) {
  const FOOTER_HEIGHT = 78
  const height = 270
  const width = 210
  const margin = 34
  const left = CBDimension.width / 2 - width / 2
  const menuTop = CBDimension.bounded.top - margin - height
  const menuBottom = CBDimension.bounded.bottom + margin + height
  let position = { width, maxHeight: height, left }
  // showing the block menu above
  if (direction === "above") {
    if (menuTop < 0) {
      // Handles when the menu may popup behind the top of the browser window
      return {
        ...position,
        top: CBDimension.top + margin / 2
      }
    }
    return {
      ...position,
      top: CBDimension.top - height - margin
    }
  }

  // showing the block menu below
  if (menuBottom > window.innerHeight - FOOTER_HEIGHT + margin) {
    // Handles when the menu may go vertically off at the bottom of the browser window
    // it also takes the footer height into account.
    return {
      ...position,
      top: CBDimension.height - height - margin / 2
    }
  }
  return {
    ...position,
    top: CBDimension.height + margin
  }
}
